import { createContext, useState, useContext, useEffect } from 'react';

// Доступные режимы темы
const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Определяем системные предпочтения
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  // Определяем начальный режим темы из localStorage или используем системный
  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode && Object.values(THEME_MODES).includes(savedMode)
      ? savedMode
      : THEME_MODES.SYSTEM;
  });

  // Определяем, тёмная ли сейчас тема (фактическое состояние)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (themeMode === THEME_MODES.LIGHT) return false;
    if (themeMode === THEME_MODES.DARK) return true;
    return prefersDark; // Для системного режима используем системные предпочтения
  });

  // Функция циклического переключения темы
  const cycleTheme = () => {
    setThemeMode(currentMode => {
      if (currentMode === THEME_MODES.LIGHT) return THEME_MODES.DARK;
      if (currentMode === THEME_MODES.DARK) return THEME_MODES.SYSTEM;
      return THEME_MODES.LIGHT;
    });
  };

  // Отслеживаем изменение системной темы
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      if (themeMode === THEME_MODES.SYSTEM) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [themeMode]);

  // Обновляем localStorage и применяем тему при изменении режима
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);

    let newIsDarkMode;
    if (themeMode === THEME_MODES.LIGHT) {
      newIsDarkMode = false;
    } else if (themeMode === THEME_MODES.DARK) {
      newIsDarkMode = true;
    } else { // THEME_MODES.SYSTEM
      newIsDarkMode = prefersDark;
    }
    setIsDarkMode(newIsDarkMode);

  }, [themeMode, prefersDark]);

  // Применяем тему к body
  useEffect(() => {
    document.body.dataset.theme = isDarkMode ? 'dark' : 'light';
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{
      isDarkMode,
      themeMode,
      cycleTheme,
      THEME_MODES // Экспортируем константы режимов
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);